/**
 * Population arithmetic: what an area has done since 1968, and what it would
 * do next under a stated set of assumptions.
 *
 * Pure, like `scenario.ts` — no React, no fetching — because this is the part
 * worth checking against published figures. The decomposition below reproduces
 * INSEE's own numbers for Paris (−0.65%/yr over 2017–2023, natural +0.55,
 * migration −1.19), which is the check that matters.
 *
 * The forward half is a scenario in the strict sense. Nothing published
 * projects a commune: Omphale needs zones of 50,000 people and stops at
 * department level, so a commune's future here is its department's official
 * trajectory, times whatever the commune has recently been doing differently —
 * faded out, because a commune that has been outrunning its department for six
 * years is not going to keep doing so for fifty.
 */

/** One census date, with the rates for the period *ending* at it. */
export interface CensusRow {
  year: number;
  pop: number;
  births: number | null;
  deaths: number | null;
  dwellings: number;
  main_res: number;
  second_res: number;
  vacant: number;
  hh_pop: number;
  area_km2: number;
  /** Length of the period these rates cover; null on the first census. */
  period_years: number | null;
  growth_rate: number | null;
  natural_rate: number | null;
  migration_rate: number | null;
  main_growth: number | null;
  hh_size: number;
  vacancy_rate: number;
  second_rate: number;
  density: number;
}

export type ProjectionScenario =
  | "central"
  | "migration_low"
  | "migration_high"
  | "population_low"
  | "population_high";

export interface DepartmentProjection {
  year: number;
  pop: number;
}

export interface AreaDemographics {
  scope: "commune" | "departement";
  code: string;
  name: string | null;
  /** Set when a neighbourhood click was answered with its commune. */
  resolved_from: "iris" | null;
  series: CensusRow[];
  department: { code: string; name: string | null; series: CensusRow[] };
  projections: Record<ProjectionScenario, DepartmentProjection[]>;
}

/**
 * How long a commune's divergence from its department is allowed to last.
 *
 * Net migration is autocorrelated — a commune gaining people for fifteen years
 * probably gains next year too — but it is not permanent: land runs out, a
 * rail line opens somewhere else, a big employer moves. Twenty years to zero
 * is a guess, and it is deliberately the one assumption the user can turn off
 * entirely by setting persistence to 0.
 */
export const DRIFT_FADE_YEARS = 20;

/** Household size has fallen for fifty years, but it cannot fall to nothing. */
const HH_SIZE_FLOOR = 1.4;
const HH_SIZE_CEILING = 4;

export const SCENARIO_LABELS: Record<ProjectionScenario, string> = {
  central: "Central",
  migration_low: "Low migration",
  migration_high: "High migration",
  population_low: "Low population",
  population_high: "High population",
};

const ln1p = (rate: number) => Math.log(1 + rate);

/** The most recent census, which every forward figure is measured from. */
export const latestCensus = (series: CensusRow[]): CensusRow | null =>
  series.length ? series[series.length - 1] : null;

/**
 * Fraction of a drift that has accumulated after `t` years.
 *
 * The drift decays linearly to zero over `fade` years, so the accumulated log
 * difference is the area under that line — `t − t²/2F` while it lasts, and a
 * constant `F/2` afterwards. Using the integral rather than a per-year decay
 * keeps the curve smooth where the fade ends, which a chart makes obvious.
 */
export function fadedYears(t: number, fade = DRIFT_FADE_YEARS): number {
  if (fade <= 0) return 0;
  return t < fade ? t - (t * t) / (2 * fade) : fade / 2;
}

export interface ProjectionParams {
  /** Which Omphale variant drives the department behind the commune. */
  scenario: ProjectionScenario;
  /** 0–1: how much of the area's recent divergence is carried forward. */
  persistence: number;
  /** Last year to project to. */
  toYear: number;
}

export interface PopulationRow {
  year: number;
  /** Recorded at a census; null between and after them. */
  pop: number | null;
  /** Recorded households, i.e. occupied main residences. */
  households: number | null;
  /** Central scenario; null before the base year. */
  projected: number | null;
  projectedHouseholds: number | null;
  /** Low-to-high official population scenarios, at the same drift. */
  band: [number, number] | null;
}

export interface Projection {
  rows: PopulationRow[];
  base: CensusRow;
  /** Annual rate the area diverged from its department by, most recent period. */
  drift: number;
  /** Department's own rate over the same period, for context. */
  departmentRate: number;
  final: { year: number; pop: number; households: number; low: number; high: number };
  /** Extra homes a year implied by the household path, at today's stock mix. */
  dwellingsPerYear: number;
}

const indexAt = (series: DepartmentProjection[], year: number): number | null => {
  const row = series.find((r) => r.year === year);
  return row ? row.pop : null;
};

/**
 * Population and households forward, hung off the department's official path.
 *
 * Three multiplied pieces, each of which can be read on its own: today's
 * population, the department's projected index, and the commune's own fading
 * divergence from it. The band is the published low-to-high population
 * scenarios — an official spread of assumptions, not a confidence interval.
 */
export function projectPopulation(
  data: AreaDemographics,
  { scenario, persistence, toYear }: ProjectionParams,
): Projection | null {
  const base = latestCensus(data.series);
  const depBase = latestCensus(data.department.series);
  if (!base || !depBase) return null;

  const chosen = data.projections[scenario] ?? [];
  const baseIndex = indexAt(chosen, base.year);
  if (!baseIndex) return null;

  // The commune's divergence from its department over the last intercensal
  // period. Both come from the same census, so the boundary restatements and
  // the census's own smoothing cancel out of the difference.
  const areaRate = base.growth_rate ?? 0;
  const departmentRate = depBase.growth_rate ?? 0;
  const drift =
    data.scope === "departement" ? 0 : ln1p(areaRate) - ln1p(departmentRate);

  // Household size continues its own recent slope, fading on the same clock —
  // it has fallen for fifty years, but a projection that lets it fall forever
  // ends up inventing households out of nothing.
  const prev = data.series[data.series.length - 2];
  const hhSlope =
    prev && base.period_years
      ? (base.hh_size - prev.hh_size) / base.period_years
      : 0;
  const householdShare = base.hh_pop / base.pop;
  const stockFactor = Math.max(
    0.5,
    1 - (base.vacancy_rate + base.second_rate),
  );

  const rows: PopulationRow[] = data.series.map((r) => ({
    year: r.year,
    pop: r.pop,
    households: r.main_res,
    projected: null,
    projectedHouseholds: null,
    band: null,
  }));

  const at = (year: number, which: ProjectionScenario) => {
    const idx = indexAt(data.projections[which] ?? [], year);
    const start = indexAt(data.projections[which] ?? [], base.year);
    if (!idx || !start) return null;
    const t = year - base.year;
    return base.pop * (idx / start) * Math.exp(drift * persistence * fadedYears(t));
  };

  let last = {
    year: base.year,
    pop: base.pop,
    households: base.main_res,
    low: base.pop,
    high: base.pop,
  };

  for (let year = base.year; year <= toYear; year++) {
    const central = at(year, scenario);
    if (central === null) break;
    const t = year - base.year;
    const hhSize = Math.min(
      HH_SIZE_CEILING,
      Math.max(HH_SIZE_FLOOR, base.hh_size + hhSlope * fadedYears(t)),
    );
    const households = (central * householdShare) / hhSize;
    const low = at(year, "population_low") ?? central;
    const high = at(year, "population_high") ?? central;

    const existing = rows.find((r) => r.year === year);
    const patch = {
      projected: central,
      projectedHouseholds: households,
      band: [Math.min(low, high), Math.max(low, high)] as [number, number],
    };
    if (existing) Object.assign(existing, patch);
    else rows.push({ year, pop: null, households: null, ...patch });

    last = { year, pop: central, households, low, high };
  }

  rows.sort((a, b) => a.year - b.year);

  const years = Math.max(1, last.year - base.year);
  const dwellingsPerYear =
    ((last.households - base.main_res) / years) / stockFactor;

  return {
    rows,
    base,
    drift: Math.exp(drift) - 1,
    departmentRate,
    final: {
      year: last.year,
      pop: last.pop,
      households: last.households,
      low: last.low,
      high: last.high,
    },
    dwellingsPerYear,
  };
}

/** A period between two censuses, with growth split into its two causes. */
export interface ComponentRow {
  /** e.g. "2017–23". */
  label: string;
  from: number;
  to: number;
  growth: number;
  natural: number;
  migration: number;
}

const shortYear = (y: number) => String(y).slice(2);

/**
 * Growth split into natural change and net migration, period by period.
 *
 * This is the whole reason for pulling births and deaths through the pipeline.
 * Two communes losing a percent a year are in completely different situations
 * if one is losing it to an age pyramid and the other to people leaving, and
 * only the second can turn around inside a decade.
 */
export function components(series: CensusRow[]): ComponentRow[] {
  const out: ComponentRow[] = [];
  for (let i = 1; i < series.length; i++) {
    const r = series[i];
    if (r.growth_rate === null || r.natural_rate === null) continue;
    out.push({
      label: `${shortYear(series[i - 1].year)}–${shortYear(r.year)}`,
      from: series[i - 1].year,
      to: r.year,
      growth: r.growth_rate,
      natural: r.natural_rate,
      migration: r.migration_rate ?? r.growth_rate - r.natural_rate,
    });
  }
  return out;
}

/** One commune in the cross-section behind the scatter. */
export interface CommunePoint {
  code: string;
  name: string;
  dep: string;
  /** A point inside the commune, so a dot in the scatter can fly the map to it. */
  lon: number;
  lat: number;
  pop: number;
  households: number;
  growth_rate: number;
  migration_rate: number;
  natural_rate: number;
  household_growth: number;
  hh_size: number;
  vacancy_rate: number;
  second_rate: number;
  density: number;
  price_growth: number;
  price_m2: number;
  price_year: number;
  sales: number;
}

export type CorrelationX =
  | "growth_rate"
  | "migration_rate"
  | "natural_rate"
  | "household_growth"
  | "density"
  | "vacancy_rate"
  | "second_rate";
export type CorrelationY = "price_growth" | "price_m2";

export interface Correlation {
  n: number;
  /** Pearson, on the raw values. */
  r: number | null;
  /** Spearman, which a handful of outlying communes cannot drag around. */
  rho: number | null;
}

const pearson = (xs: number[], ys: number[]): number | null => {
  const n = xs.length;
  if (n < 3) return null;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < n; i++) {
    const a = xs[i] - mx;
    const b = ys[i] - my;
    num += a * b;
    dx += a * a;
    dy += b * b;
  }
  return dx === 0 || dy === 0 ? null : num / Math.sqrt(dx * dy);
};

/** Ranks, averaging ties, so Spearman is not skewed by repeated values. */
function ranks(values: number[]): number[] {
  const order = values.map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
  const out: number[] = Array.from({ length: values.length });
  let i = 0;
  while (i < order.length) {
    let j = i;
    while (j + 1 < order.length && order[j + 1].v === order[i].v) j++;
    const rank = (i + j) / 2 + 1;
    for (let k = i; k <= j; k++) out[order[k].i] = rank;
    i = j + 1;
  }
  return out;
}

export function correlate(
  points: CommunePoint[],
  x: CorrelationX,
  y: CorrelationY,
): Correlation {
  const xs = points.map((p) => p[x]);
  const ys = points.map((p) => p[y]);
  return {
    n: points.length,
    r: pearson(xs, ys),
    rho: pearson(ranks(xs), ranks(ys)),
  };
}

/**
 * Below this a commune's own numbers are noise.
 *
 * A village of 800 has census confidence intervals wide enough to swamp any
 * trend in it, and DVF suppresses its sales anyway. The threshold is a default,
 * not a rule — it is exposed in the panel because how small is too small
 * depends on the question.
 */
export const MIN_POP_DEFAULT = 5000;

/** How strong a correlation actually is, in words rather than a bare number. */
export function strengthLabel(r: number | null): string {
  if (r === null) return "no fit";
  const a = Math.abs(r);
  if (a < 0.1) return "no relationship";
  if (a < 0.3) return "weak";
  if (a < 0.5) return "moderate";
  if (a < 0.7) return "strong";
  return "very strong";
}
